import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  CheckCircle2,
  Clock,
  Loader2,
  Package,
  Server,
  Tag,
  Truck,
  Wallet,
  XCircle,
  AlertCircle,
  Coins,
  Crown,
} from 'lucide-react';
import { orderService, PlayerOrderDetail, OrderDelivery } from '@/lib/services/order.service';
import { useStore } from '@/contexts/StoreContext';

interface OrderDetailModalProps {
  orderId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function formatPrice(cents: number) {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(
    cents / 100,
  );
}

function formatDate(dateStr: string | Date) {
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(dateStr));
}

function useOrderStatus(t: (key: string) => string) {
  const statusMap: Record<string, { label: string; icon: React.ElementType; className: string }> = {
    PAID: {
      label: t('store.order_detail.status_paid'),
      icon: CheckCircle2,
      className: 'bg-success/10 text-success border-success/20',
    },
    DELIVERED: {
      label: t('store.order_detail.status_delivered'),
      icon: CheckCircle2,
      className: 'bg-success/10 text-success border-success/20',
    },
    PENDING: {
      label: t('store.order_detail.status_pending'),
      icon: Clock,
      className: 'bg-warning/10 text-warning border-warning/20',
    },
    CONFIRMED: {
      label: t('store.order_detail.status_confirmed'),
      icon: CheckCircle2,
      className: 'bg-primary/10 text-primary border-primary/20',
    },
    PROCESSING: {
      label: t('store.order_detail.status_processing'),
      icon: Loader2,
      className: 'bg-primary/10 text-primary border-primary/20',
    },
    SHIPPED: {
      label: t('store.order_detail.status_shipped'),
      icon: Truck,
      className: 'bg-accent/10 text-accent border-accent/20',
    },
    CANCELLED: {
      label: t('store.order_detail.status_cancelled'),
      icon: XCircle,
      className: 'bg-destructive/10 text-destructive border-destructive/20',
    },
    EXPIRED: {
      label: t('store.order_detail.status_expired'),
      icon: Clock,
      className: 'bg-muted text-muted-foreground border-border',
    },
    REFUNDED: {
      label: t('store.order_detail.status_refunded'),
      icon: XCircle,
      className: 'bg-muted text-muted-foreground border-border',
    },
  };

  return (status: string) =>
    statusMap[status] ?? {
      label: status,
      icon: Clock,
      className: 'bg-muted/10 text-muted-foreground border-muted/20',
    };
}

function useDeliveryStatus(t: (key: string) => string) {
  const map: Record<string, { label: string; className: string }> = {
    DELIVERED: {
      label: t('store.order_detail.delivery_status_delivered'),
      className: 'bg-success/10 text-success border-success/20',
    },
    PENDING: {
      label: t('store.order_detail.delivery_status_pending'),
      className: 'bg-warning/10 text-warning border-warning/20',
    },
    FAILED: {
      label: t('store.order_detail.delivery_status_failed'),
      className: 'bg-destructive/10 text-destructive border-destructive/20',
    },
  };

  return (status: string) =>
    map[status] ?? { label: status, className: 'bg-muted/10 text-muted-foreground' };
}

function resolvePaymentLabel(
  t: (key: string) => string,
  method: string | null | undefined,
  creditUsed: number,
  total: number,
): string {
  const map: Record<string, string> = {
    PIX: t('store.order_detail.payment_pix'),
    CREDIT_CARD: t('store.order_detail.payment_credit_card'),
    MERCADO_PAGO: t('store.order_detail.payment_mercado_pago'),
    PAYPAL: t('store.order_detail.payment_paypal'),
    PICPAY: t('store.order_detail.payment_picpay'),
    FREE: t('store.order_detail.payment_free'),
    CREDITS: t('store.order_detail.payment_credits'),
    COUPON: t('store.order_detail.payment_coupon'),
  };

  if (method) return map[method] ?? method;
  // Fallback para pedidos antigos sem paymentMethod gravado
  if (creditUsed > 0) return t('store.order_detail.payment_credits');
  if (total === 0) return t('store.order_detail.payment_free');
  return '—';
}

function DeliveryRow({ delivery, t }: { delivery: OrderDelivery; t: (key: string) => string }) {
  const getDeliveryStatus = useDeliveryStatus(t);
  const statusCfg = getDeliveryStatus(delivery.status);

  return (
    <div
      data-testid="order-detail-delivery-row"
      className="flex flex-col gap-2 p-4 rounded-lg bg-muted/30 border border-border/50"
    >
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2 text-sm font-medium">
          <Server className="w-4 h-4 text-primary shrink-0" />
          <span>{delivery.serverName ?? t('store.order_detail.delivery_server')}</span>
        </div>
        <Badge className={`text-xs gap-1 ${statusCfg.className}`}>
          {statusCfg.label}
        </Badge>
      </div>
      {delivery.deliveredAt && (
        <p className="text-xs text-muted-foreground">
          {t('store.order_detail.delivery_delivered_at')}: {formatDate(delivery.deliveredAt)}
        </p>
      )}
    </div>
  );
}

export function OrderDetailModal({ orderId, open, onOpenChange }: OrderDetailModalProps) {
  const { t } = useTranslation();
  const { store } = useStore();
  const [order, setOrder] = useState<PlayerOrderDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const getStatusConfig = useOrderStatus(t);

  useEffect(() => {
    if (!open || !orderId || !store?.id) return;

    setOrder(null);
    setError(false);
    setLoading(true);

    orderService
      .getOrderById(store.id, orderId)
      .then((data) => {
        if (data) {
          setOrder(data);
        } else {
          setError(true);
        }
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [open, orderId, store?.id]);

  const statusConfig = order ? getStatusConfig(order.status) : null;
  const StatusIcon = statusConfig?.icon ?? Clock;

  const hasDiscount = order && order.discountAmount > 0;
  const hasCoupon = order?.couponCode;
  const hasCreditUsed = order && order.creditUsed > 0;
  const hasDeliveries = order && order.deliveries.length > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="order-detail-modal"
        className="max-w-2xl w-full p-0 gap-0 overflow-hidden"
      >
        <DialogHeader className="px-6 pt-6 pb-4 border-b border-border/50">
          <DialogTitle className="flex items-center gap-3 text-xl">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
              <Package className="w-5 h-5 text-primary" />
            </div>
            <div>
              <p className="font-bold">{t('store.order_detail.title')}</p>
              {order && (
                <p className="text-sm font-normal text-muted-foreground">
                  #{order.id.slice(0, 8).toUpperCase()}
                </p>
              )}
            </div>
          </DialogTitle>
        </DialogHeader>

        <ScrollArea className="max-h-[75vh]">
          {loading && (
            <div className="flex flex-col items-center justify-center py-16 gap-3">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
              <p className="text-sm text-muted-foreground">{t('store.order_detail.loading')}</p>
            </div>
          )}

          {error && !loading && (
            <div className="flex flex-col items-center justify-center py-16 gap-3">
              <AlertCircle className="w-8 h-8 text-destructive" />
              <p className="text-sm text-muted-foreground">{t('store.order_detail.error')}</p>
            </div>
          )}

          {order && !loading && (
            <div className="p-6 space-y-6">

              {/* Summary */}
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                <div className="space-y-1">
                  <p className="text-xs text-muted-foreground uppercase tracking-wide">
                    {t('store.order_detail.date')}
                  </p>
                  <p className="text-sm font-medium">{formatDate(order.createdAt)}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-xs text-muted-foreground uppercase tracking-wide">
                    {t('store.order_detail.status')}
                  </p>
                  <Badge
                    data-testid="order-detail-status"
                    className={`gap-1 text-xs ${statusConfig?.className}`}
                  >
                    <StatusIcon className="w-3 h-3" />
                    {statusConfig?.label}
                  </Badge>
                </div>
                <div className="space-y-1">
                  <p className="text-xs text-muted-foreground uppercase tracking-wide">
                    {t('store.order_detail.payment_method')}
                  </p>
                  <p className="text-sm font-medium">
                    {resolvePaymentLabel(t, order.paymentMethod, order.creditUsed, order.total)}
                  </p>
                </div>
                {order.paymentId && (
                  <div className="space-y-1 col-span-2 sm:col-span-3">
                    <p className="text-xs text-muted-foreground uppercase tracking-wide">
                      {t('store.order_detail.payment_id')}
                    </p>
                    <p className="text-xs font-mono text-muted-foreground break-all">
                      {order.paymentId}
                    </p>
                  </div>
                )}
              </div>

              <Separator />

              {/* Items */}
              <div className="space-y-3">
                <p className="text-sm font-semibold flex items-center gap-2">
                  <Package className="w-4 h-4 text-primary" />
                  {t('store.order_detail.items_title')}
                </p>
                <div className="space-y-2">
                  {order.items.map((item, idx) => (
                    <div
                      key={item.id ?? `item-${idx}`}
                      data-testid="order-detail-item"
                      className="flex items-center justify-between gap-4 p-3 rounded-lg bg-muted/30 border border-border/50"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-8 h-8 rounded-md bg-primary/10 flex items-center justify-center shrink-0">
                          {item.type === 'CREDIT' ? (
                            <Coins className="w-4 h-4 text-primary" />
                          ) : item.type === 'VIP_UPGRADE' ? (
                            <Crown className="w-4 h-4 text-primary" />
                          ) : (
                            <Package className="w-4 h-4 text-primary" />
                          )}
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-medium truncate">{item.productName}</p>
                          {item.categoryName && (
                            <p className="text-xs text-muted-foreground">{item.categoryName}</p>
                          )}
                        </div>
                      </div>
                      <div className="text-right shrink-0">
                        <p className="text-sm font-semibold text-primary">
                          {formatPrice(item.total)}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {item.quantity}x {formatPrice(item.price)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <Separator />

              {/* Financial Summary */}
              <div className="space-y-2 text-sm">
                <div className="flex justify-between text-muted-foreground">
                  <span>{t('store.order_detail.subtotal')}</span>
                  <span>{formatPrice(order.subtotal)}</span>
                </div>

                {hasDiscount && (
                  <div className="flex justify-between text-success gap-2">
                    <span className="flex items-center gap-1">
                      <Tag className="w-3.5 h-3.5" />
                      {hasCoupon
                        ? `${t('store.order_detail.coupon')}: ${order.couponCode}`
                        : t('store.order_detail.discount')}
                    </span>
                    <span>-{formatPrice(order.discountAmount)}</span>
                  </div>
                )}

                {hasCreditUsed && (
                  <div className="flex justify-between text-success gap-2">
                    <span className="flex items-center gap-1">
                      <Wallet className="w-3.5 h-3.5" />
                      {t('store.order_detail.credit_used')}
                    </span>
                    <span>-{formatPrice(order.creditUsed)}</span>
                  </div>
                )}

                <Separator />

                <div className="flex justify-between font-bold text-base">
                  <span>{t('store.order_detail.total')}</span>
                  <span className="text-primary">{formatPrice(order.total)}</span>
                </div>
              </div>

              {/* Deliveries */}
              <div className="space-y-3">
                <p className="text-sm font-semibold flex items-center gap-2">
                  <Truck className="w-4 h-4 text-primary" />
                  {t('store.order_detail.delivery_title')}
                </p>
                {hasDeliveries ? (
                  <div className="space-y-2">
                    {order.deliveries.map((delivery) => (
                      <DeliveryRow key={delivery.id} delivery={delivery} t={t} />
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    {t('store.order_detail.no_deliveries')}
                  </p>
                )}
              </div>
            </div>
          )}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
